import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { useEntitlements } from '@/contexts/EntitlementsContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  billingApi,
  SubscriptionPlan,
  BillingInterval,
  PlanSlug,
  FeatureKey,
} from '@/services/billingApi';
import { Button } from '@heroui/react';
import { Loading } from '@/components/Loading';

interface BillingPlansProps {
  onSelectTab?: (tab: 'overview' | 'plans') => void;
}

const FEATURE_LABELS: Array<{ key: FeatureKey; label: string }> = [
  { key: 'pos', label: 'Core POS & Register Terminal' },
  { key: 'sync', label: 'Cloud & Offline Sync' },
  { key: 'inventory', label: 'Inventory & Stock Tracking' },
  { key: 'expenses', label: 'Expense Recording & Receipts' },
  { key: 'basicReports', label: 'Basic Sales Reports' },
  { key: 'exports', label: 'CSV & Spreadsheet Data Exports' },
  { key: 'purchaseOrders', label: 'Supplier Purchase Orders & Replenishment' },
  { key: 'onlineStore', label: 'Online Storefront Catalog' },
  { key: 'advancedReports', label: 'Advanced Profit & Loss & Margin Reports' },
  { key: 'onlineCheckout', label: 'Online Checkout & Digital Payments' },
  { key: 'rota', label: 'Staff Rota, Shift Scheduling & Leaves' },
  { key: 'payroll', label: 'Automated UK Payroll & PAYE Submissions' },
  { key: 'vatReturns', label: 'HMRC Making Tax Digital (MTD) VAT Returns' },
  { key: 'foodSafety', label: 'Food Safety & Hygiene Compliance Logs' },
  { key: 'training', label: 'Interactive Staff Training Hub' },
];

export const getPlanHighlights = (plan: SubscriptionPlan): string[] => {
  const f = plan.features || {};
  const highlights: string[] = [];

  if (typeof f.maxLocations === 'number' || typeof f.maxStaff === 'number') {
    const locText =
      typeof f.maxLocations === 'number'
        ? `${f.maxLocations} Store Location${f.maxLocations > 1 ? 's' : ''}`
        : null;
    const staffText =
      typeof f.maxStaff === 'number' ? `up to ${f.maxStaff} staff` : null;
    highlights.push([locText, staffText].filter(Boolean).join(', '));
  }

  if (typeof f.maxRegisters === 'number' || typeof f.maxProducts === 'number') {
    const regText =
      typeof f.maxRegisters === 'number'
        ? `${f.maxRegisters} Register${f.maxRegisters > 1 ? 's' : ''}`
        : null;
    const prodText =
      typeof f.maxProducts === 'number'
        ? `up to ${Number(f.maxProducts).toLocaleString()} products`
        : null;
    highlights.push([regText, prodText].filter(Boolean).join(', '));
  }

  for (const { key, label } of FEATURE_LABELS) {
    if (f[key] === true) {
      highlights.push(label);
    }
  }

  return highlights;
};

const BillingPlans: React.FC<BillingPlansProps> = () => {
  const { entitlements, plan: activePlan, loading: entitlementsLoading } = useEntitlements();
  const { user } = useAuth();
  const isOwner = user?.role === 'OWNER';

  const [interval, setInterval] = useState<BillingInterval>(
    entitlements?.subscription?.billingInterval || 'MONTHLY'
  );
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [processingSlug, setProcessingSlug] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (entitlements?.subscription?.billingInterval) {
      setInterval(entitlements.subscription.billingInterval);
    }
  }, [entitlements?.subscription?.billingInterval]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const data = await billingApi.listPlans();
        setPlans(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setErrorMsg(err.message || 'Could not load subscription plans.');
      } finally {
        setLoading(false);
      }
    };

    void fetchPlans();
  }, []);

  const subStatus = entitlements?.subscription?.status;
  // Only mark a plan as the user's current plan when they have an active/billing paid subscription
  const hasPaidSubscription =
    subStatus === 'ACTIVE' || subStatus === 'PAST_DUE' || subStatus === 'GRACE_PERIOD';
  const currentPlanSlug = hasPaidSubscription
    ? activePlan?.slug || entitlements?.subscription?.plan?.slug || null
    : null;
  const currentInterval = entitlements?.subscription?.billingInterval || 'MONTHLY';

  const handleSelectPlan = async (targetPlan: SubscriptionPlan) => {
    if (!isOwner) {
      setErrorMsg('Only the business owner can change subscription plans.');
      return;
    }

    try {
      setProcessingSlug(targetPlan.slug);
      setErrorMsg(null);
      const res = await billingApi.createCheckout({
        planSlug: targetPlan.slug as PlanSlug,
        interval,
      });

      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to start plan checkout. Please try again.');
      setProcessingSlug(null);
    }
  };

  if (loading || (entitlementsLoading && !entitlements)) {
    return (
      <div className="py-24 flex justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 pb-10">
      {errorMsg && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-sm flex items-center justify-between">
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)} className="text-rose-500 font-bold ml-2">
            ×
          </button>
        </div>
      )}

      {/* Interval Toggle */}
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="inline-flex items-center p-1 bg-gray-100 rounded-xl border border-gray-200">
          <button
            onClick={() => setInterval('MONTHLY')}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
              interval === 'MONTHLY'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly Billing
          </button>
          <button
            onClick={() => setInterval('YEARLY')}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
              interval === 'YEARLY'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>Annual Billing</span>
            <span className="bg-emerald-100 text-emerald-700 text-[10px] font-extrabold px-1.5 py-0.2 rounded-full">
              2 Months Free
            </span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
        {plans.map((planItem) => {
          const isCurrentPlan = Boolean(currentPlanSlug && planItem.slug === currentPlanSlug);
          const isSameInterval = currentInterval === interval;
          const isCurrent = isCurrentPlan && isSameInterval;
          const isPopular = planItem.slug === 'shop_plus';
          const isProcessing = processingSlug === planItem.slug;

          const priceMonthly = Number(planItem.priceMonthly);
          const priceYearly =
            planItem.priceYearly !== undefined && planItem.priceYearly !== null
              ? Number(planItem.priceYearly)
              : priceMonthly * 12;
          const displayPrice = interval === 'YEARLY' ? priceYearly : priceMonthly;
          const highlights = getPlanHighlights(planItem);

          return (
            <div
              key={planItem.slug}
              className={`relative flex flex-col bg-white rounded-2xl border p-6 md:p-8 transition-all shadow-sm ${
                isCurrentPlan
                  ? 'border-2 border-blue-600 ring-4 ring-gray-100'
                  : isPopular
                  ? 'border-2 border-primary/60 shadow-md'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {isPopular && !isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-white text-[11px] font-bold tracking-wide shadow-sm">
                  Most Popular
                </div>
              )}

              {isCurrentPlan && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-blue-600 text-white text-[11px] font-bold tracking-wide shadow-sm">
                  Current Plan
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-gray-900">{planItem.name}</h3>
              </div>

              <p className="text-xs text-gray-500 mb-6 min-h-[36px]">{planItem.description}</p>

              <div className="flex items-baseline gap-1.5 mb-6">
                <span className="text-4xl font-extrabold text-gray-900">£{displayPrice}</span>
                <span className="text-gray-500 font-medium text-sm">
                  /{interval === 'YEARLY' ? 'year' : 'month'}
                </span>
                {interval === 'YEARLY' && (
                  <span className="text-xs text-emerald-600 font-bold ml-2">
                    (£{(displayPrice / 12).toFixed(2)}/mo)
                  </span>
                )}
              </div>

              <div className="border-t border-gray-100 pt-6 flex flex-col gap-3 mb-8 flex-1">
                <p className="text-xs text-gray-500 mb-1">What's included:</p>
                {highlights.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 leading-normal">{feature}</span>
                  </div>
                ))}
              </div>

              <div>
                {isCurrent ? (
                  <Button isDisabled className="w-full py-2.5 rounded-full cursor-default">
                    Current Plan
                  </Button>
                ) : isOwner ? (
                  <Button
                    onPress={() => handleSelectPlan(planItem)}
                    isDisabled={isProcessing}
                    className={`w-full py-2.5 rounded-full transition-all shadow-sm flex items-center justify-center gap-2 ${
                      isPopular
                        ? 'bg-primary text-white hover:bg-primary/90'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    } disabled:opacity-50`}
                  >
                    {isProcessing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <span>
                        {isCurrentPlan
                          ? `Switch to ${interval === 'YEARLY' ? 'Annual' : 'Monthly'}`
                          : hasPaidSubscription
                          ? `Switch to ${planItem.name}`
                          : `Choose ${planItem.name}`}
                      </span>
                    )}
                  </Button>
                ) : (
                  <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-200 text-[11px] text-gray-500 flex items-center justify-center gap-1.5">
                    <ShieldAlert size={14} className="text-gray-400 shrink-0" />
                    <span>Owner required to upgrade</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BillingPlans;
